// Sugalarm — operator broadcast banner (cycle 97).
//
// Renders the active `admin_broadcast` across kiosk / web / mobile. It is
// placed in normal layout flow by KioskFrame/MobileFrame (flex-shrink: 0)
// so it RESERVES height and the content below reflows — never an overlay
// that hides glucose data. Dismiss is local (per broadcast id); an
// `expires_at` in the past or a server clear frame removes it everywhere.

import { useEffect, useState } from "react";

import type { ActiveBroadcast } from "../lib/types";
import type { FrameVariant } from "../hooks/useFrameVariant";
import styles from "./BroadcastBanner.module.css";

export function BroadcastBanner({
  broadcast,
  variant,
  now,
}: {
  broadcast: ActiveBroadcast | null;
  variant: FrameVariant;
  now: number;
}): JSX.Element | null {
  const [dismissedId, setDismissedId] = useState<string | null>(null);

  // Reset the local dismiss when a new broadcast arrives.
  useEffect(() => {
    if (broadcast && dismissedId && broadcast.id !== dismissedId) {
      setDismissedId(null);
    }
  }, [broadcast, dismissedId]);

  if (!broadcast) return null;
  if (dismissedId === broadcast.id) return null;
  if (
    broadcast.expires_at &&
    new Date(broadcast.expires_at).getTime() <= now
  ) {
    return null;
  }

  const levelClass =
    broadcast.level === "urgent"
      ? styles.urgent
      : broadcast.level === "warn"
        ? styles.warn
        : styles.info;
  const kioskClass = variant === "kiosk" ? styles.kiosk : "";

  return (
    <div
      className={`${styles.banner} ${levelClass ?? ""} ${kioskClass ?? ""}`}
      role="status"
      aria-live="polite"
    >
      <span className={styles.dot} />
      <span className={styles.msg}>{broadcast.message}</span>
      <button
        className={styles.dismiss}
        onClick={() => setDismissedId(broadcast.id)}
        aria-label="Dismiss broadcast"
      >
        ×
      </button>
    </div>
  );
}
